import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { FiCheck, FiX, FiClock, FiUser, FiCalendar, FiAlertCircle, FiGrid, FiLock } from 'react-icons/fi';
import { formatDateForDisplay, getChicagoTimeString, formatTimeTo12Hour } from '../../utils/datetime-comprehensive';
import apiService from '../../services/api';
import QRCodeScanner from '../common/QRCodeScanner';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  background: var(--bg-primary);
  padding: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CheckInCard = styled(motion.div)`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1rem;
  margin: 0;
`;

const SessionInfo = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const SessionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
`;

const SessionDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SessionDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const CheckInSection = styled.div`
  margin-bottom: 2rem;
`;

const CheckInTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CheckInButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CheckInButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: var(--bg-secondary);
    border-color: var(--primary-color);
    transform: translateY(-1px);
  }
  
  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
    
    &:hover {
      background: var(--primary-hover);
    }
  }
  
  &.success {
    background: rgba(104, 211, 145, 0.1);
    color: var(--success-dark);
    border-color: var(--success-color);
    
    &:hover {
      background: rgba(104, 211, 145, 0.2);
    }
  }
  
  &.error {
    background: rgba(252, 129, 129, 0.1);
    color: var(--error-color);
    border-color: var(--error-color);
    
    &:hover {
      background: rgba(252, 129, 129, 0.2);
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled(motion.div)`
  padding: 1rem;
  border-radius: 0;
  font-size: 0.875rem;
  text-align: center;
  margin-bottom: 1rem;
  
  &.success {
    background: rgba(104, 211, 145, 0.1);
    border: 1px solid var(--success-color);
    color: var(--success-color);
  }
  
  &.error {
    background: rgba(252, 129, 129, 0.1);
    border: 1px solid var(--error-color);
    color: var(--error-color);
  }
  
  &.info {
    background: rgba(96, 165, 250, 0.1);
    border: 1px solid var(--info-color);
    color: var(--info-color);
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 0;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const EmptyDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
`;

const TimeWindowInfo = styled.div`
  background: rgba(96, 165, 250, 0.1);
  border: 1px solid var(--info-color);
  border-radius: 0;
  padding: 1rem;
  margin: 1rem 0;
`;

const TimeWindowTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--info-color);
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TimeWindowDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: var(--info-color);
`;

const TimeWindowDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TimeWindowRestricted = styled.div`
  background: rgba(252, 129, 129, 0.1);
  border: 1px solid var(--error-color);
  border-radius: 0;
  padding: 1rem;
  margin: 1rem 0;
  text-align: center;
`;

const TimeWindowRestrictedTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--error-color);
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const TimeWindowRestrictedMessage = styled.p`
  font-size: 0.875rem;
  color: var(--error-dark);
  margin: 0;
  line-height: 1.4;
`;

function AttendanceCheckIn() {
  console.log('🔍 AttendanceCheckIn component function called');
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  console.log('🔍 AttendanceCheckIn component loaded with sessionId:', sessionId);
  console.log('🔍 Current URL:', window.location.href);
  console.log('🔍 User state:', user);
  console.log('🔍 Auth loading:', authLoading);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [attendanceStatus, setAttendanceStatus] = useState(null);

  // Redirect to login if not authenticated
  useEffect(() => {
    console.log('🔍 AttendanceCheckIn - Auth state:', { user, authLoading, sessionId });
    if (!authLoading && !user) {
      const currentPath = `/attendance/checkin/${sessionId}`;
      console.log('🔍 Redirecting to login with path:', currentPath);
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
    } else if (user) {
      console.log('🔍 User is authenticated, proceeding with check-in');
    }
  }, [user, authLoading, navigate, sessionId]);

  const fetchSessionData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiService.getAttendanceSession(sessionId);
      
      if (response.success) {
        setSession(response.session);
        
        console.log('🔍 Session data loaded:', {
          session: response.session,
          checkinStart: response.session.checkin_start_time,
          checkinEnd: response.session.checkin_end_time,
          hasTimeWindow: !!(response.session.checkin_start_time && response.session.checkin_end_time)
        });
        
        // Check if student is already checked in
        const studentRecord = response.session.records.find(
          record => record.student_id === apiService.getCurrentUser()?.id
        );
        
        if (studentRecord) {
          setAttendanceStatus(studentRecord.status);
        }
        
        // No automatic check-in validation - let the user manually check in when they click the button
      } else {
        setError(response.message || 'Session not found');
      }
    } catch (error) {
      console.error('Error fetching session:', error);
      setError('Failed to load attendance session');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId && user) {
      fetchSessionData();
    }
  }, [sessionId, fetchSessionData, user]);

  const handleCheckIn = async (status = 'present') => {
    try {
      setCheckingIn(true);
      setError('');
      setSuccess('');
      
      console.log('🔍 Attempting to check in:', { sessionId, status, userId: user?.id });
      
      const response = await apiService.checkInAttendance(sessionId, status);
      
      console.log('🔍 Check-in response:', response);
      console.log('🔍 Check-in response details:', JSON.stringify(response, null, 2));
      
      if (response.success) {
        setSuccess('Successfully checked in for attendance!');
        setAttendanceStatus(status);
        
        // Redirect after successful check-in
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        console.error('🔍 Check-in failed:', response);
        console.error('🔍 Check-in failed details:', JSON.stringify(response, null, 2));
        setError(response.message || 'Failed to check in');
      }
    } catch (error) {
      console.error('🔍 Error checking in:', error);
      console.error('🔍 Error details:', {
        message: error.message,
        status: error.status,
        response: error.response
      });
      // Show the actual error message from the server with better formatting
      let errorMessage = error.message || 'Failed to check in. Please try again.';
      
      // Temporary debugging - show the raw error message
      console.log('🔍 RAW ERROR MESSAGE:', errorMessage);
      
      console.log('🔍 Error message formatting:', {
        originalMessage: error.message,
        sessionExists: !!session,
        checkinStartTime: session?.checkin_start_time
      });
      
      // If it's a time window error, use the server's formatted message directly
      if (error.message && (
        error.message.includes('Check-in is not available at this time') || 
        error.message.includes('Check-in opens in') ||
        error.message.includes('Check-in window is available during') ||
        error.message.includes('Check-in window has closed') ||
        error.message.includes('Check-in is not available. This session is for')
      )) {
        // Use the server's formatted message directly as it's already user-friendly
        errorMessage = error.message;
        console.log('🔍 Using server time window error message:', errorMessage);
      }
      
      setError(errorMessage);
    } finally {
      setCheckingIn(false);
    }
  };

  const handleQRScan = (data) => {
    // Verify the scanned data matches the current session
    const expectedUrl = `${window.location.origin}/portal/attendance/checkin/${sessionId}`;
    
    if (data === expectedUrl) {
      handleCheckIn('present');
    } else {
      setError('Invalid QR code. Please scan the correct attendance QR code.');
    }
  };

  const isWithinTimeWindow = () => {
    if (!session) {
      console.log('🔍 No session data');
      return false;
    }

    // Use check-in times if available, otherwise fall back to session times
    const startTime = session.checkin_start_time || session.start_time;
    const endTime = session.checkin_end_time || session.end_time;

    if (!startTime || !endTime) {
      console.log('🔍 No time window restrictions');
      return true; // No time restrictions
    }

    // Get current time in Chicago timezone
    const currentTime = getChicagoTimeString();
    const checkinStart = startTime;
    const checkinEnd = endTime;

    const isWithin = currentTime >= checkinStart && currentTime <= checkinEnd;
    
    console.log('🔍 Time window check:', {
      currentTime,
      checkinStart,
      checkinEnd,
      isWithin
    });

    return isWithin;
  };


  const getTimeWindowStatus = () => {
    if (!session || !session.checkin_start_time || !session.checkin_end_time) {
      return null;
    }

    // Get current time in Chicago timezone
    const currentTime = getChicagoTimeString();
    const checkinStart = session.checkin_start_time;
    const checkinEnd = session.checkin_end_time;

    if (currentTime < checkinStart) {
      const startTime = new Date(`2000-01-01T${checkinStart}`);
      const currentTimeObj = new Date(`2000-01-01T${currentTime}`);
      const minutesUntilStart = Math.ceil((startTime - currentTimeObj) / (1000 * 60));
      
      return {
        type: 'before',
        message: `Check-in opens in ${minutesUntilStart} minutes`,
        startTime: formatTimeTo12Hour(checkinStart),
        endTime: formatTimeTo12Hour(checkinEnd)
      };
    } else if (currentTime > checkinEnd) {
      const endTime = new Date(`2000-01-01T${checkinEnd}`);
      const currentTimeObj = new Date(`2000-01-01T${currentTime}`);
      const minutesSinceEnd = Math.ceil((currentTimeObj - endTime) / (1000 * 60));
      
      return {
        type: 'after',
        message: `Check-in closed ${minutesSinceEnd} minutes ago`,
        startTime: formatTimeTo12Hour(checkinStart),
        endTime: formatTimeTo12Hour(checkinEnd)
      };
    }

    return {
      type: 'available',
      message: 'Check-in is available now',
      startTime: formatTimeTo12Hour(checkinStart),
      endTime: formatTimeTo12Hour(checkinEnd)
    };
  };

  const getStatusMessage = () => {
    if (attendanceStatus === 'present') {
      return 'You have already checked in as present for this session.';
    } else if (attendanceStatus === 'late') {
      return 'You have already checked in as late for this session.';
    }
    // Don't show message for 'absent' status as it's the default
    return null;
  };

  const getStatusColor = () => {
    if (attendanceStatus === 'present') return 'success';
    if (attendanceStatus === 'late') return 'info';
    if (attendanceStatus === 'absent') return 'info'; // Changed from 'error' to 'info'
    return 'info';
  };

  // Don't render anything if not authenticated (will redirect)
  if (!user && !authLoading) {
    console.log('🔍 No user and not loading, returning null');
    return null;
  }

  if (loading || authLoading) {
    return (
      <Container>
        <CheckInCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <LoadingSpinner />
            <p>Loading attendance session...</p>
          </div>
        </CheckInCard>
      </Container>
    );
  }

  if (error && !session) {
    return (
      <Container>
        <CheckInCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <EmptyState>
            <EmptyIcon>⚠️</EmptyIcon>
            <EmptyTitle>Session Not Found</EmptyTitle>
            <EmptyDescription>
              The attendance session you're looking for doesn't exist or you don't have access to it.
            </EmptyDescription>
            <CheckInButton 
              className="primary" 
              onClick={() => navigate('/dashboard')}
              style={{ marginTop: '1.5rem' }}
            >
              <FiX size={16} />
              Go to Dashboard
            </CheckInButton>
          </EmptyState>
        </CheckInCard>
      </Container>
    );
  }

  return (
    <Container>
      <CheckInCard
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Header>
          <Title>
            <FiCheck size={24} />
            Attendance Check-In
          </Title>
          <Subtitle>Check in for your attendance session</Subtitle>
        </Header>

        {session && (
          <SessionInfo>
            <SessionTitle>{session.title || `${session.course_name} - ${session.course_code}`}</SessionTitle>
            <SessionDetails>
              <SessionDetail>
                <FiCalendar size={16} />
                {formatDateForDisplay(session.date, 'M/d/yyyy')}
              </SessionDetail>
              <SessionDetail>
                <FiUser size={16} />
                {session.instructor_first_name} {session.instructor_last_name}
              </SessionDetail>
              <SessionDetail>
                <FiClock size={16} />
                {(() => {
                  // Use check-in times if available, otherwise fall back to session times
                  const startTime = session.checkin_start_time || session.start_time;
                  const endTime = session.checkin_end_time || session.end_time;
                  
                  if (startTime && endTime) {
                    return `${formatTimeTo12Hour(startTime)} - ${formatTimeTo12Hour(endTime)}`;
                  }
                  
                  return 'No time restrictions';
                })()}
              </SessionDetail>
            </SessionDetails>
          </SessionInfo>
        )}

        {session && getTimeWindowStatus() && !error && (
          <>
            {getTimeWindowStatus().type === 'available' ? (
              <TimeWindowInfo>
                <TimeWindowTitle>
                  <FiClock size={16} />
                  Check-in Time Window
                </TimeWindowTitle>
                <TimeWindowDetails>
                  <TimeWindowDetail>
                    <FiClock size={14} />
                    Available: {getTimeWindowStatus().startTime} - {getTimeWindowStatus().endTime}
                  </TimeWindowDetail>
                  <TimeWindowDetail>
                    <FiCheck size={14} />
                    {getTimeWindowStatus().message}
                  </TimeWindowDetail>
                </TimeWindowDetails>
              </TimeWindowInfo>
            ) : (
              <TimeWindowRestricted>
                <TimeWindowRestrictedTitle>
                  <FiLock size={16} />
                  Check-in Not Available
                </TimeWindowRestrictedTitle>
                <TimeWindowRestrictedMessage>
                  {getTimeWindowStatus().message}
                </TimeWindowRestrictedMessage>
                <TimeWindowDetails style={{ marginTop: '0.5rem', color: 'var(--error-dark)' }}>
                  <TimeWindowDetail>
                    <FiClock size={14} />
                    Window: {getTimeWindowStatus().startTime} - {getTimeWindowStatus().endTime}
                  </TimeWindowDetail>
                </TimeWindowDetails>
              </TimeWindowRestricted>
            )}
          </>
        )}

        {error && (
          <StatusMessage className="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <FiAlertCircle size={16} style={{ marginRight: '0.5rem' }} />
            {error}
          </StatusMessage>
        )}

        {success && (
          <StatusMessage className="success" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <FiCheck size={16} style={{ marginRight: '0.5rem' }} />
            {success}
          </StatusMessage>
        )}

        {attendanceStatus && getStatusMessage() && (
          <StatusMessage className={getStatusColor()} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {getStatusMessage()}
          </StatusMessage>
        )}

        {(!attendanceStatus || attendanceStatus === 'absent') && (
          <CheckInSection>
            <CheckInTitle>
              <FiGrid size={20} />
              Check In Options
            </CheckInTitle>
            <CheckInButtons>
              <CheckInButton 
                className="primary" 
                onClick={() => setShowScanner(true)}
                disabled={checkingIn || !isWithinTimeWindow()}
              >
                <FiGrid size={20} />
                Scan QR Code
              </CheckInButton>
              <CheckInButton 
                onClick={() => handleCheckIn('present')}
                disabled={checkingIn || !isWithinTimeWindow()}
              >
                {checkingIn ? <LoadingSpinner /> : <FiCheck size={20} />}
                Check In as Present
              </CheckInButton>
            </CheckInButtons>
          </CheckInSection>
        )}

        <CheckInButton 
          onClick={() => navigate('/dashboard')}
          style={{ width: '100%', marginTop: '1rem' }}
        >
          <FiX size={16} />
          Back to Dashboard
        </CheckInButton>
      </CheckInCard>

      {showScanner && (
        <QRCodeScanner
          isOpen={showScanner}
          onScan={handleQRScan}
          onError={(err) => setError('Failed to access camera. Please try manual check-in.')}
          onClose={() => setShowScanner(false)}
          title="Scan Attendance QR Code"
          description="Point your camera at the QR code displayed by your instructor"
        />
      )}
    </Container>
  );
}

export default AttendanceCheckIn;
